<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col md="12">
          <v-card elevation="0" rounded="xxl" class="app-card">
            <v-card-title>
              <AppPageHeader
                icon="mdi-folder-outline"
                :title="isDeletePage() ? 'Deleted Categories' : 'Categories'"
                subtitle="Top-level groupings for everything you sell"
                style="width: 100%"
              >
                <v-btn
                  v-if="!isDeletePage() && can('CATEGORIES.CREATE')"
                  fab
                  x-small
                  color="primary"
                  @click="showCreateDialog = true"
                >
                  <v-icon dark>mdi-plus</v-icon>
                </v-btn>
              </AppPageHeader>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="6">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Search ... "
                    color="#e60040"
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="clearSearch"
                    @keydown.enter="getData"
                  ></v-text-field>
                </v-col>
                <v-col sm="3">
                  <v-autocomplete
                    ref="status"
                    solo
                    rounded
                    v-model="filter.is_active"
                    :items="statuses"
                    clearable
                    @click:clear="clearStatus"
                    label="Status"
                    @input="getData"
                    prepend-inner-icon="mdi-toggle-switch"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col md="12">
          <v-card elevation="0" rounded="xxl" class="app-card">
            <v-card-text>
              <section v-if="isLoading" style="height: 285px">
                <v-progress-linear
                  indeterminate
                  color="primary"
                ></v-progress-linear>
              </section>
              <v-simple-table
                v-else
                fixed-header
                height="500"
                class="striped-table"
              >
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-start">Sr No</th>
                      <th class="text-start">Categories</th>
                      <th class="text-start">Level</th>
                      <th class="text-start">Add Subcategory</th>
                      <th class="text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody v-if="categories.length">
                    <tr
                      v-for="(category, i) in categories"
                      :key="`category_${i}`"
                      :class="{
                        'table-row-even': i % 2 === 0,
                        'table-row-odd': i % 2 !== 0,
                        'table-row-last': i === categories.length - 1,
                      }"
                    >
                      <td style="min-width: 85px">{{ i + 1 }}</td>
                      <td>{{ category.name || "N/A" }}</td>
                      <td>
                        <v-chip small color="success">
                          Level {{ category.level || 1 }}
                        </v-chip>
                      </td>
                      <td>
                        <v-btn
                          class="mx-2 mb-2"
                          color="primary"
                          small
                          :to="`/categories/${encryptIt(
                            category.id
                          )}/sub-categories`"
                        >
                          Add New
                        </v-btn>
                      </td>
                      <td class="text-right">
                        <template v-if="!isDeletePage()">
                          <v-tooltip bottom v-if="can('CATEGORIES.UPDATE')">
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                class="mr-2"
                                v-bind="attrs"
                                v-on="on"
                                fab
                                x-small
                                color="primary"
                                @click="openEdit(category)"
                              >
                                <v-icon dark>mdi-pencil</v-icon>
                              </v-btn>
                            </template>
                            <span>Edit this Category</span>
                          </v-tooltip>
                          <v-tooltip bottom v-if="can('CATEGORIES.DELETE')">
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                fab
                                x-small
                                color="error"
                                @click="askDelete(category.id)"
                              >
                                <v-icon dark>mdi-delete</v-icon>
                              </v-btn>
                            </template>
                            <span>Delete this Category</span>
                          </v-tooltip>
                        </template>
                        <template v-else>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                v-bind="attrs"
                                v-on="on"
                                fab
                                x-small
                                color="success"
                                @click="restoreCategory(category.id)"
                              >
                                <v-icon dark>mdi-delete-restore</v-icon>
                              </v-btn>
                            </template>
                            <span>Restore this Category</span>
                          </v-tooltip>
                        </template>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="5">
                        <AppEmptyState
                          icon="mdi-folder-outline"
                          :title="isDeletePage() ? 'No deleted categories' : 'No categories yet'"
                          message="Categories organize your products for browsing and reporting."
                          :action-text="!isDeletePage() && can('CATEGORIES.CREATE') ? 'Add Category' : ''"
                          @action="showCreateDialog = true"
                        />
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-pagination
                rounded
                v-model="page"
                :length="paginationLength"
              ></v-pagination>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Delete confirmation -->
    <ConfirmationDialog
      :show.sync="deleteDialog"
      title="Delete Category"
      message="Are you sure you want to delete this category? This action cannot be undone."
      icon="mdi-delete"
      color="error"
      :loading="delete_Category"
      @confirm="Delete_this(pendingDeleteId)"
    />

    <!-- Add Category -->
    <AppDialog
      :show.sync="showCreateDialog"
      max-width="600"
      icon="mdi-folder-outline"
      title="Add Category"
      subtitle="Create a new top-level grouping for your products"
    >
      <CategoryCreatePage
        v-if="showCreateDialog"
        embedded
        @saved="onDialogSaved"
        @close="showCreateDialog = false"
      />
    </AppDialog>

    <!-- Edit Category -->
    <AppDialog
      :show.sync="showEditDialog"
      max-width="600"
      icon="mdi-folder-outline"
      :title="editingRow ? `Edit ${editingRow.name}` : 'Edit Category'"
      subtitle="Update this category's name"
    >
      <CategoryEditPage
        v-if="showEditDialog"
        embedded
        :category-id="editingId"
        @saved="onDialogSaved"
        @close="showEditDialog = false"
      />
    </AppDialog>
  </v-main>
</template>

<script>
import axios from "axios";
import ConfirmationDialog from "@/components/ConfirmationDialog.vue";
import AppDialog from "@/components/AppDialog.vue";
import AppEmptyState from "@/components/AppEmptyState.vue";
import AppPageHeader from "@/components/AppPageHeader.vue";
import CategoryCreatePage from "./create.vue";
import CategoryEditPage from "./id.vue";

export default {
  name: "CategoriesPage",
  components: { ConfirmationDialog, AppDialog, AppEmptyState, AppPageHeader, CategoryCreatePage, CategoryEditPage },
  data() {
    return {
      showCreateDialog: false,
      showEditDialog: false,
      editingId: null,
      editingRow: null,
      filter: {
        search_keyword: "",
        is_active: "",
        show_deleted: false,
      },
      categories: [], // Initialize the categories property
      deleteDialog: false,
      pendingDeleteId: null,
      statuses: [
        {
          id: 0,
          text: "Select Active Status",
          value: "",
        },
        { id: "ACTIVE", text: "ACTIVE", value: "1" },
        { id: "INACTIVE", text: "INACTIVE", value: "0" },
      ],
      page: 1,
      serialNoStarting: 1,
      paginationLength: 0,
      count: 0,
      isLoading: false,
      delete_Category: false,
      is_deleted_Category: false,
      restore_Category: false,
      is_restored_Category: false,
    };
  },
  watch: {
    page: "getData",
    $route() {
      this.getData();
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      this.filter.show_deleted = this.$route.query.open_trash ? 1 : 0;
      this.filter.search_keyword = this.filter.search_keyword || "";
      this.filter.is_active = this.filter.is_active || "";
      let query = `page=${this.page}&rowsPerPage=10&show_deleted=${this.filter.show_deleted}&is_active=${this.filter.is_active}&search_keyword=${this.filter.search_keyword}`;

      try {
        const response = await axios.get(`categories?${query}`);
        console.log("got:", response.data.data);
        // this.categories = response.data.data.filter(
        //   (category) => category.parent_id == null
        // );
        if (response.data && Array.isArray(response.data.data)) {
          this.categories = response.data.data.filter(
            (category) => category.parent_id == null
          );
        } else {
          console.error(
            "Expected array at response.data.data, but got:",
            response.data.data
          );
          this.categories = []; // or handle appropriately
        }
        console.log("category", this.categories);

        this.count = response.data.count;
        this.paginationLength = Math.ceil(this.count / 10);
      } catch (error) {
        console.error(error);
      }

      this.isLoading = false;
    },
    askDelete(id) {
      this.pendingDeleteId = id;
      this.deleteDialog = true;
    },
    async Delete_this(id) {
      this.delete_Category = true;
      try {
        await axios.post(`/categories/delete/${this.encryptIt(id)}`);
        this.deleteDialog = false;
        this.getData();
      } catch (error) {
        console.error(error);
      } finally {
        this.delete_Category = false;
      }
    },
    openEdit(category) {
      this.editingId = this.encryptIt(category.id);
      this.editingRow = category;
      this.showEditDialog = true;
    },
    onDialogSaved() {
      this.showCreateDialog = false;
      this.getData();
      if (this.$toast) {
        this.$toast.success("Category saved successfully");
      }
    },
    async restoreCategory(id) {
      this.restore_position = true;
      try {
        await axios.post(`/categories/restore/${this.encryptIt(id)}`);
        this.getData();
      } catch (error) {
        console.error(error);
      }
    },
    clearSearch() {
      this.filter.search_keyword = "";
      this.page = 1;
      this.getData();
    },
    clearStatus() {
      this.filter.is_active = "";
      this.page = 1;
      this.getData();
    },
  },
};
</script>
